import { hasFocusClass, postContentClass } from "@repo/fika-blog/src/classes"
import { extensions } from "@repo/fika-blog/src/extensions"
import {
  LensesExtension,
  lensesKey,
} from "@repo/fika-blog/src/extensions/lenses"
import {
  ProofreadExtension,
  proofreadKey,
} from "@repo/fika-blog/src/extensions/proofread"
import type { Editor, JSONContent } from "@repo/fika-blog/src/tiptap/core"
import { parseLocale } from "@repo/fika-shared/src/locale"
import type { UploadFolder } from "@repo/fika-shared/src/upload-folder.js"
import BubbleMenu from "@tiptap/extension-bubble-menu"
import CharacterCount from "@tiptap/extension-character-count"
import TiptapFileHandler from "@tiptap/extension-file-handler"
import Focus from "@tiptap/extension-focus"
import TiptapUniqueID from "@tiptap/extension-unique-id"
import { Placeholder, TrailingNode } from "@tiptap/extensions"
import {
  type Accessor,
  createSignal,
  type Setter,
  Show,
  untrack,
} from "solid-js"
import { createEditorTransaction, createTiptapEditor } from "solid-tiptap"
import { AnimatePresence } from "@/designSystem/components/animate-presence"
import { useI18nContext } from "@/i18n/i18n-solid"
import { onDrop, onPaste } from "../image-upload"
import { getEditCodeBlock } from "./code-block-edit-node"
import { getEditEmbed } from "./embed-edit-node"
import { getEditImage } from "./image-edit-node"
import { getEditMathBlock, getEditMathInline } from "./math-edit-node"
import { BlockNodeMenu } from "./menus/block-node-menu"
import { CalloutEmojiMenu } from "./menus/callout-emoji-menu"
import { LensMenu } from "./menus/lens-menu"
import { LinkMenu } from "./menus/link-menu"
import { ProofreadMenu } from "./menus/proofread-menu"
import { getEditSnippet } from "./snippet-edit-node"
import { menuClass } from "./styles.css"
import { getEditSubscribe } from "./subscribe-edit-node"
import {
  dropCursorClass,
  editorClass,
  editorWrapperClass,
} from "./tiptap-editor.css"

type WysiwygEditorProps = {
  setEditorRef: Setter<HTMLDivElement | undefined>
  setLinkBubbleMenuRef: Setter<HTMLDivElement | undefined>
  setProofreadMenuRef?: Setter<HTMLDivElement | undefined>
  setLensMenuRef?: Setter<HTMLDivElement | undefined>
  editor?: Editor
  class?: string
  disableFloatingMenu?: boolean
  uploadFolder: Accessor<UploadFolder>
}
export function WysiwygEditor(props: WysiwygEditorProps) {
  const linkKey = createEditorTransaction(
    () => props.editor,
    editor => {
      if (!editor) return false
      const { selection } = editor.state
      const { $from, $to, empty } = selection

      // Show link menu when cursor is positioned within a link (no selection)
      if (empty) {
        // Check if cursor position has a link mark
        const linkMark = $from.marks().find(mark => mark.type.name === "link")
        return !!linkMark
      }

      // Check if the entire selection is within a link
      // This means all text nodes in the range must have the link mark
      let allNodesHaveLink = true
      let hasAnyNode = false

      editor.state.doc.nodesBetween($from.pos, $to.pos, node => {
        // Only check text nodes
        if (node.isText) {
          hasAnyNode = true
          const hasLinkMark = node.marks.some(mark => mark.type.name === "link")
          if (!hasLinkMark) {
            allNodesHaveLink = false
            return false // Stop iterating
          }
        }
      })

      return hasAnyNode && allNodesHaveLink
    },
  )

  return (
    <div class={editorWrapperClass}>
      <div
        ref={props.setEditorRef}
        class={props.class}
        autocorrect="off"
        spellcheck="false"
      />
      <div ref={props.setProofreadMenuRef} class={menuClass}>
        <Show when={props.disableFloatingMenu ? false : props.editor}>
          {editor => (
            <AnimatePresence
              from={{ opacity: 0, y: 10 }}
              to={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              enterOptions={{ delay: 0.2 }}
              keepInitialPosition
            >
              <ProofreadMenu editor={editor()} />
            </AnimatePresence>
          )}
        </Show>
      </div>
      <div ref={props.setLensMenuRef} class={menuClass}>
        <Show when={props.disableFloatingMenu ? false : props.editor}>
          {editor => (
            <AnimatePresence
              from={{ opacity: 0, y: 10 }}
              to={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              enterOptions={{ delay: 0.2 }}
              keepInitialPosition
            >
              <LensMenu editor={editor()} />
            </AnimatePresence>
          )}
        </Show>
      </div>
      <div ref={props.setLinkBubbleMenuRef} class={menuClass}>
        <Show when={props.disableFloatingMenu ? false : props.editor}>
          {editor => (
            <Show when={linkKey()} keyed>
              <AnimatePresence
                from={{ opacity: 0, y: 10 }}
                to={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                enterOptions={{ delay: 0.2 }}
                keepInitialPosition
              >
                <LinkMenu editor={editor()} />
              </AnimatePresence>
            </Show>
          )}
        </Show>
      </div>
      <BlockNodeMenu
        editor={props.editor}
        disableFloatingMenu={props.disableFloatingMenu}
        uploadFolder={props.uploadFolder}
      />
      <CalloutEmojiMenu editor={props.editor} />
    </div>
  )
}

export function createEditor(props: {
  content?: Accessor<JSONContent>
  onUpdate?: (editor: Editor) => void
  userName?: string
  uploadFolder: Accessor<UploadFolder>
  blogLocale?: string
  autoFocusOnCreate?: boolean
}) {
  const { LL } = useI18nContext()
  const [editorRef, setEditorRef] = createSignal<HTMLDivElement>()
  const [linkMenuRef, setLinkMenuRef] = createSignal<HTMLDivElement>()
  const [proofreadMenuRef, setProofreadMenuRef] = createSignal<HTMLDivElement>()
  const [lensMenuRef, setLensMenuRef] = createSignal<HTMLDivElement>()
  const userName = props.userName || ""

  const translations = {
    snippet: {
      placeholder: LL().snippet.placeholder(),
      placeholderUpdate: LL().snippet.placeholderUpdate(),
      buttonCreate: LL().snippet.buttonCreate(),
      buttonUpdate: LL().snippet.buttonUpdate(),
      buttonCreating: LL().snippet.buttonCreating(),
      buttonUpdating: LL().snippet.buttonUpdating(),
      delete: LL().common.delete(),
    },
    editor: {
      defaultPlaceholder: LL().editor.defaultPlaceholder(),
    },
  }

  const editorInstance = createTiptapEditor(() => ({
    element: editorRef()!,
    content: untrack(() => (props.content ? props.content() : undefined)),
    autofocus: false,
    onCreate: ({ editor }) => {
      if (props.autoFocusOnCreate) {
        const isEmpty = editor.getText().trim().length === 0

        if (isEmpty) {
          editor.commands.focus()
        } else {
          editor.commands.setTextSelection(0)
        }
      }
    },
    onUpdate: ({ editor }) => {
      props.onUpdate?.(editor)
    },
    editable: true,
    editorProps: {
      attributes: {
        class: `${editorClass} ${postContentClass}`,
      },
      handleClickOn: (_view, _pos, node, _nodePos, event, direct) => {
        if (!direct) return false

        const type = node.type.name
        if (type !== "blockquote" && type !== "callout") return false

        const target =
          event.target instanceof HTMLElement ? event.target : undefined
        if (target?.closest("[data-block-node-menu-trigger='true']"))
          return false

        // We intentionally block direct node-click behavior for these block nodes.
        // Interaction for block actions now lives in the six-dots menu.
        return true
      },
      handleDOMEvents: {
        blur: (_view, event) => {
          const relatedTarget = event.relatedTarget as HTMLElement | null
          if (!relatedTarget) return false

          const menuRefs = [linkMenuRef()]

          for (const menuRef of menuRefs) {
            if (menuRef?.contains(relatedTarget)) {
              return true
            }
          }

          return false
        },
      },
    },
    extensions: [
      ...extensions({
        dropcursor: {
          color: false,
          width: 2,
          class: dropCursorClass,
        },
        Snippet: getEditSnippet({
          translations: translations.snippet,
        }),
        Subscribe: getEditSubscribe({
          userName,
        }),
        CodeBlock: getEditCodeBlock(),
        MathBlock: getEditMathBlock(),
        MathInline: getEditMathInline(),
        Embed: getEditEmbed(),
        Image: getEditImage({
          placeholder: LL().editor.imageTitlePlaceholder(),
          altPlaceholder: LL().editor.imageDescriptionPlaceholder(),
          sizeLabels: {
            small: LL().editor.imageSizeSmall(),
            medium: LL().editor.imageSizeMedium(),
            big: LL().editor.imageSizeBig(),
          },
        }),
      }),
      TiptapUniqueID.configure({
        attributeName: "uid",
        types: [
          "image",
          "paragraph",
          "heading",
          "blockquote",
          "callout",
          "subscribe",
        ],
      }),
      BubbleMenu.extend({ name: "linkBubbleMenu" }).configure({
        element: linkMenuRef(),
        pluginKey: "linkMenu",
        updateDelay: 200,
        shouldShow({ state }) {
          const { selection } = state
          const { $from, $to, empty } = selection

          // Show link menu when cursor is positioned within a link (no selection)
          if (empty) {
            // Check if cursor position has a link mark
            const linkMark = $from
              .marks()
              .find(mark => mark.type.name === "link")
            return !!linkMark
          }

          // Check if the entire selection is within a link
          // This means all text nodes in the range must have the link mark
          let allNodesHaveLink = true
          let hasAnyNode = false

          state.doc.nodesBetween($from.pos, $to.pos, node => {
            // Only check text nodes
            if (node.isText) {
              hasAnyNode = true
              const hasLinkMark = node.marks.some(
                mark => mark.type.name === "link",
              )
              if (!hasLinkMark) {
                allNodesHaveLink = false
                return false // Stop iterating
              }
            }
          })

          return hasAnyNode && allNodesHaveLink
        },
      }),
      BubbleMenu.extend({ name: "proofreadBubbleMenu" }).configure({
        element: untrack(proofreadMenuRef)!,
        pluginKey: "proofreadMenu",
        updateDelay: 200,
        shouldShow({ state }) {
          const { selection } = state
          const { $from, empty } = selection

          if (!empty) return false

          const pluginState = proofreadKey.getState(state)
          if (!pluginState?.decorations) return false

          const decorations = pluginState.decorations
          const pos = $from.pos

          const foundDecoration = decorations.find(pos, pos)
          return foundDecoration !== null
        },
      }),
      BubbleMenu.extend({ name: "lensBubbleMenu" }).configure({
        element: untrack(lensMenuRef)!,
        pluginKey: "lensMenu",
        updateDelay: 200,
        shouldShow({ state }) {
          const { selection } = state
          const { $from, empty } = selection

          if (!empty) return false

          const pluginState = lensesKey.getState(state) as
            | {
                decorations: {
                  find: (
                    from: number,
                    to: number,
                  ) => Array<{ spec?: { lensType?: string } }>
                }
                lensType: string | null
              }
            | undefined
          if (!pluginState?.lensType) return false

          const decorations = pluginState.decorations
          const pos = $from.pos

          const found = decorations.find(pos, pos)
          return found.some(d => d.spec?.lensType)
        },
      }),
      Placeholder.configure({
        placeholder: translations.editor.defaultPlaceholder,
      }),
      Focus.configure({ className: hasFocusClass, mode: "deepest" }),
      TiptapFileHandler.configure({
        onDrop: (editor, files, pos) => {
          const uploadFolder = props.uploadFolder()
          onDrop({ editor, files, pos, uploadFolder })
        },
        onPaste: (editor, files) => {
          const uploadFolder = untrack(() => props.uploadFolder())
          return onPaste({ editor, files, uploadFolder })
        },
      }),
      CharacterCount,
      TrailingNode,
      ProofreadExtension,
      LensesExtension.configure({
        locale: parseLocale(props.blogLocale),
      }),
    ],
  }))

  const editor = editorInstance

  const wysiwygProps = {
    get editor() {
      return editor()
    },
    setEditorRef,
    setLinkBubbleMenuRef: setLinkMenuRef,
    uploadFolder: props.uploadFolder,
    setProofreadMenuRef,
    setLensMenuRef,
  }

  return { editor, wysiwygProps }
}
